import moment from 'moment'
import store from '@/store'
import { padStart } from 'lodash'

const currentYear = new Date().getFullYear()

function createIntlFormatter(factory) {
  let formatter
  store.watch(
    () => store.state.locale || undefined,
    locale => {
      formatter = factory(locale)
    },
    {
      immediate: true,
    },
  )
  return value => formatter.format(value)
}

function formatWithGradualUnit(units, value, base, forceUnit = 'auto') {
  let index
  if (forceUnit !== 'auto') {
    index = units.indexOf(forceUnit)
    if (index < 0) throw new Error('Invalid unit')
  } else {
    index = Math.min(Math.floor(Math.log(value) / Math.log(base)), units.length - 1)
  }
  return [formatInteger(value / Math.pow(base, index)), units[index]]
}

//
// Format integers
//
const integerFormatter = createIntlFormatter(
  locale => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
)
const units = ['', 'K', 'M', 'G', 'T']
export function formatInteger(n, kind) {
  if (typeof n !== 'number') return n
  switch (kind) {
    case 'short':
      return formatWithGradualUnit(units, n, 1000).join('')
    default:
      return integerFormatter(n)
  }
}

//
// Format floating point numbers
//
const smallFloatFormatter = createIntlFormatter(
  locale => new Intl.NumberFormat(locale, { maximumSignificantDigits: 2 }),
)
const mediumFloatFormatter = createIntlFormatter(
  locale => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }),
)
export function formatFloat(n) {
  if (typeof n !== 'number') return n
  const abs = Math.abs(n)
  if (abs < 0) {
    // Keep only 3 significant digits for small numbers
    return smallFloatFormatter(n)
  }
  if (abs < 100) {
    return mediumFloatFormatter(n)
  }
  // Else keep only the integer part
  return formatInteger(n)
}

//
// Format absolute time
//
const timeFormatter = createIntlFormatter(
  locale =>
    new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }),
)
const shortTimeFormatter = createIntlFormatter(
  locale =>
    new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: 'numeric',
    }),
)
export function formatTime(date, kind) {
  let formatter
  switch (kind) {
    case 'short':
      formatter = shortTimeFormatter
      break
    default:
      formatter = timeFormatter
  }
  return formatter(date)
}

export function formatDateTime(date, kind) {
  if (date === 'now') return 'now'
  return moment(date).format('dddd Do MMM hh:mm a')
}

export function formatDateTimeForLogs(date, kind) {
  if (date === 'now') return 'now'
  return moment(date).format('YYYY-MM-DD hh:mm a')
}

//
// Format absolute date
//
const dateFormatter = createIntlFormatter(
  locale =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
)
const dateShortFormatter = createIntlFormatter(
  locale =>
    new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
    }),
)
const dateShortYearFormatter = createIntlFormatter(
  locale =>
    new Intl.DateTimeFormat(locale, {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
    }),
)
export function formatDate(date, kind) {
  if (date === 'now') return 'now'
  let formatter
  switch (kind) {
    case 'short':
      formatter = date.getFullYear() !== currentYear ? dateShortYearFormatter : dateShortFormatter
      break
    default:
      formatter = dateFormatter
  }
  return formatter(date)
}

//
// Format relative date
//
export function formatRelativeDate(date) {
  // TODO use Intl.RelativeTimeFormat when available
  return moment(date).fromNow()
}

function createMeasurementFormatter(formatter) {
  return (rawValue, options) => {
    if (typeof rawValue === 'string' && rawValue) {
      const parsed = Number(rawValue)
      if (!isNaN(parsed)) rawValue = parsed
    }

    let valueAndUnit
    if (typeof rawValue === 'number' && !isNaN(rawValue)) {
      valueAndUnit = formatter(rawValue, options)
    } else {
      valueAndUnit = [rawValue === false ? 'false' : rawValue || 'N/A', '']
    }
    return valueAndUnit[0] + valueAndUnit[1]
  }
}

//
// Format bytes
//
const bytesUnits = ['B', 'KiB', 'MiB', 'GiB', 'TiB']
export const formatBytes = createMeasurementFormatter((value, options) => {
  if (value <= 0) return [0, 'B']
  return formatWithGradualUnit(bytesUnits, value, 1024, options && options.unit)
})

//
// Format duration
//
const unitDurationFormatter = createMeasurementFormatter(rawValue => {
  if (rawValue < 1000) {
    return [formatFloat(rawValue), 'ms']
  }
  if (rawValue < 60000) {
    return [formatFloat(rawValue / 1000), 's']
  }
  return [formatFloat(rawValue / 60e3), 'm']
})
export function formatDuration(value, kind) {
  switch (kind) {
    case 'hms': {
      value = (value / 1000) | 0
      const s = value % 60
      const m = ((value - s) / 60) % 60
      const h = (value - m * 60 - s) / 3600
      if (h === 0) {
        return `${padStart(m, 2, '0')}:${padStart(s, 2, '0')}`
      } else {
        return `${padStart(h, 2, '0')}:${padStart(m, 2, '0')}:${padStart(s, 2, '0')}`
      }
    }
    default:
      return unitDurationFormatter(value)
  }
}

//
// Format percent
//
export const formatPercent = createMeasurementFormatter(rawValue => {
  return [formatInteger(rawValue), '%']
})

//
// Format any metric
//
/* Basically, the unit cache is to bypass the metrics reactivity, because metrics units are likely
 * to *not* change, so we don't need to re-render whatever component is using this at each realtime
 * update */
const unitCache = new Map()
store.watch(
  () => {
    const bucket = store.getters.currentBucket
    return bucket && bucket.metrics
  },

  metrics => {
    if (!metrics) return
    for (const metricName in metrics) {
      unitCache.set(metricName, metrics[metricName].unit)
    }
  },
  { immediate: true },
)

export function getMetricUnit(metric) {
  return metric === 'cpu' ? '%' : metric === 'memory' ? 'bytes' : unitCache.get(metric) || ''
}

export function createMetricFormatter(metric) {
  const unit = getMetricUnit(metric)
  if (unit === '%') return formatPercent
  if (unit === 'bytes') return formatBytes
  if (unit === 'ms') return formatDuration
  return createMeasurementFormatter(rawValue => [formatFloat(rawValue), unit])
}

export function formatDateForURL(date) {
  return date === 'now' ? 'now' : date.toISOString().split('.')[0] + 'Z'
}
